import React, {useEffect, useState} from 'react'
import { cloneDeep } from 'lodash'
import Button from 'components/shared/Button'
import VisibilityIcon from '@material-ui/icons/Visibility'
import FmcsaDrawer from '../Summary/Summaries/SummaryDrawers/FmcsaDrawer'
import { fmcsaDetailColumn } from 'constants/fmcsa'
import TableComponent from 'components/shared/TableComponent'
import { DownloadFMCSAReport } from 'components/PdfTemplates'
import styles from './fmcsa.module.scss'
import strings from 'strings'
import Loader from "../../../../shared/Loader";
import AuthService from 'tools/AuthService'
import {Alert} from "@material-ui/lab";
import Moment from "react-moment";
import {useAppContext} from "../../../../../context/AppContext";
import { resetFmcsaData } from '../../../../../actions/applicants'
import { useDispatch } from 'react-redux'
import {Icon} from "../../../../../styles";
import request from 'tools/request'
import ExtensiveFmcsaDrawer from './ExtensiveFmcsaDrawer'
import ExtensiveSearchList from './ExtensiveSearchList'

const FmcsaVerification = (props) => {
    const {
        getFmcsaDataByApplication,
        requestFmcsaDataByApplication,
        applicationId,
        dot,
        updateSummaryCard,
        creditUsageEnabled,
        creditCosts
    } = props
    const [show, setShow] = useState(false)
    const [loading, setLoading] = useState(true)
    const [fmcsaSearchData, setFmcsaSearchData] = useState([])
    const [fmcsaExtraContent, setFmcsaExtraContent] = useState([])
    const [fmcsaRequestDate, setFmcsaRequestDate] = useState([])
    const [fmcsaRequestedBy, setFmcsaRequestedBy] = useState(null)
    const fmcsaData = { content: { carrier: fmcsaSearchData }, extraContent: fmcsaExtraContent,
        retrievalDate: fmcsaRequestDate, requestedBy: fmcsaRequestedBy }
    const { isDemoMode } = useAppContext()
    const dispatch = useDispatch()

    // Extensive FMCSA Search State
    const fmcsaExtensiveSearchEnabled = AuthService.getClientFeatures().fmcsaExtensiveSearchEnabled;
    const [extensiveSearchLoading, setExtensiveSearchLoading] = useState(false)
    const [extensiveSearchList, setExtensiveSearchList] = useState([])
    const [extensiveSearchListLoading, setExtensiveSearchListLoading] = useState(false)
    const [extensiveSearchData, setExtensiveSearchData] = useState(null)
    const [extensiveRequestedAt, setExtensiveRequestedAt] = useState(null)
    const [showExtensiveDrawer, setShowExtensiveDrawer] = useState(false)

    /*
     * Deep cloning for fmcsa columns
     * Adding action to columns
     */
    const columns = cloneDeep(fmcsaDetailColumn)
    const action = {
        formatter: (cell, row) => {
            return (
                <>
                    <VisibilityIcon color='primary' onClick={() => {
                        setShow(true);
                    }} />
                    {row?.dot &&
                        <DownloadFMCSAReport applicationId={applicationId} mode='icon'/>
                    }
                </>
            )
        }
    }

    useEffect(() => {
        if (applicationId) {
            getFMCSAData()
            fetchExtensiveSearchList()
        }
        // eslint-disable-next-line
    }, [applicationId])

    columns.map((item, index) => {
        if (item.dataField === 'action') {
            columns[index] = { ...item, ...action }
        }
        return item
    })

    /* Building Table Row Data with basic fmcsa Detail */
    const fmcsaDetailData = [
        {
            businessName: fmcsaSearchData?.legalName,
            address: fmcsaSearchData?.phyStreet,
            city: fmcsaSearchData?.phyCity,
            state: fmcsaSearchData?.phyState,
            zip: fmcsaSearchData?.phyZipcode,
            ein: fmcsaSearchData?.ein,
            mc: fmcsaExtraContent?.mcNumbers?.join(', '),
            dot: fmcsaSearchData?.dotNumber,
            requestedBy: fmcsaRequestedBy
        }
    ]

    const getFmcsaSearchCreditCost = () => {
        if (!creditCosts) {
            return 0;
        }
        const costObj = creditCosts.find(cost => cost.requestType === 'FMCSA_SEARCH');
        return costObj ? costObj.creditCost : 0;
    }

    const getExtensiveFmcsaSearchCreditCost = () => {
        if (!creditCosts) {
            return 0;
        }
        const costObj = creditCosts.find(cost => cost.requestType === 'FMCSA_EXTENSIVE_SEARCH');
        return costObj ? costObj.creditCost : 0;
    }

    const renderCreditCostEndIcon = (cost) => (
        <span className="creditCostEndIcon">
            <span className="creditCostValue">{cost}</span>
            <Icon folder="icons" fileName="credits.svg" />
        </span>
    )

    const requestFMCSADataButton = () => {
        return (
            <div className={styles.fmcsaVerificationButton}>
                <Button
                    onClick={() => requestFMCSAData()}
                    variant='contained'
                    color='primary'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                        {strings.REQUEST_FMCSA_DATA}
                        {creditUsageEnabled && renderCreditCostEndIcon(getFmcsaSearchCreditCost())}
                    </div>
                </Button>
            </div>
        )
    }

    const requestFMCSAData = () => {
        setLoading(true)
        requestFmcsaDataByApplication(applicationId, isDemoMode)
            .then(res => {
                setFmcsaSearchData(res?.content[0]?.carrier)
                setFmcsaExtraContent(res?.extraContent)
                setFmcsaRequestDate(res?.retrievalDate)
                setFmcsaRequestedBy(res?.requestedBy)
            })
            .finally(() => {
                setLoading(false)
                updateSummaryCard()
                dispatch(resetFmcsaData())
            })
    }

    const getFMCSAData = () => {
        if (loading){
            setLoading(true)
            getFmcsaDataByApplication(applicationId).then(res => {
                setFmcsaSearchData(res?.content?.carrier)
                setFmcsaExtraContent(res?.extraContent)
                setFmcsaRequestDate(res?.retrievalDate)
                setFmcsaRequestedBy(res?.requestedBy)
            }).finally(() => {
                setLoading(false)
            })
        }
    }

    // Fetch list of extensive searches
    const fetchExtensiveSearchList = async () => {
        setExtensiveSearchListLoading(true)
        try {
            const res = await request(
                `applications/${applicationId}/fmcsa/extensive-search`,
                'GET',
                null,
                false,
                { 'x-demo-mode': isDemoMode },
                null,
                false
            )
            setExtensiveSearchList(res || [])
        } catch (error) {
            setExtensiveSearchList([])
        } finally {
            setExtensiveSearchListLoading(false)
        }
    }

    // Request a new extensive search
    const requestNewExtensiveSearch = async () => {
        setExtensiveSearchLoading(true)
        try {
            const res = await request(
                `applications/${applicationId}/fmcsa/extensive-search`,
                'POST',
                null,
                false,
                { 'x-demo-mode': isDemoMode },
                null,
                false
            )

            // Refresh the list after creating a new search
            await fetchExtensiveSearchList()

            // Open drawer with the response data
            if (res) {
                setExtensiveSearchData(res)
                setExtensiveRequestedAt(new Date().toISOString())
                setShowExtensiveDrawer(true)
            }
        } catch (error) {
        } finally {
            setExtensiveSearchLoading(false)
        }
    }

    // View details of a specific extensive search
    const viewExtensiveSearchDetails = async (extensiveId) => {
        if (!extensiveId) {
            return
        }

        setExtensiveSearchLoading(true)
        try {
            const res = await request(
                `applications/${applicationId}/fmcsa/extensive-search/${extensiveId}`,
                'GET',
                null,
                false,
                { 'x-demo-mode': isDemoMode },
                null,
                false
            )
            // Open drawer with the response data
            setExtensiveSearchData(res)
            setExtensiveRequestedAt(extensiveSearchList.find(s => s.uuid === extensiveId)?.createdAt || null)
            setShowExtensiveDrawer(true)
        } catch (error) {
        } finally {
            setExtensiveSearchLoading(false)
        }
    }

    const requestExtensiveFMCSADataButton = () => {
        return (
            <div className={styles.fmcsaVerificationButton}>
                <Button
                    onClick={() => requestNewExtensiveSearch()}
                    variant='contained'
                    color='primary'
                    disabled={extensiveSearchLoading}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                        {extensiveSearchLoading ? strings.LOADING : (strings.REQUEST_EXTENSIVE_FMCSA_DATA || 'Request Extensive FMCSA Data')}
                        {creditUsageEnabled && renderCreditCostEndIcon(getExtensiveFmcsaSearchCreditCost())}
                    </div>
                </Button>
            </div>
        )
    }

    const noDotWarning = <Alert severity="info">{strings.NO_DOT_WARNING}</Alert>;

    return (
        <div className={styles.fmcsaVerificationContainer}>
            {!AuthService.isReadOnlyUser() &&
                <div>
                    {!dot && noDotWarning}
                </div>
            }
            <div>
                {loading ? <Loader/> :
                    <div>
                        <TableComponent
                            title={<>{strings.FMCSA_VERIFICATION}
                                {fmcsaRequestDate &&
                                    <span className={styles.fmcsaRequestDateField}> {strings.LAST_DATA_RETRIEVAL} {
                                        <Moment format="MMM DD YYYY">{fmcsaRequestDate}</Moment>}
                                    </span>
                                }
                            </>}
                            keyField='businessName'
                            columns={columns}
                            data={fmcsaDetailData}
                            showPagination={false}
                            className={styles.customTable}
                        />
                    </div>
                }
                <FmcsaDrawer
                    open={show}
                    onClose={() => setShow(false)}
                    applicantId={applicationId}
                    data={fmcsaData}
                    dotNumber={dot}
                    hasDot={Boolean(dot)}
                    loading={loading}
                    onRequestFmcsa={requestFMCSAData}
                    requestingFmcsa={loading}
                    onExtensiveRequested={fetchExtensiveSearchList}
                />
                {/* Request FMCSA Data button - aligned right */}
                {!AuthService.isReadOnlyUser() && dot && requestFMCSADataButton()}
            </div>

            {/* Extensive Search Section */}
            {fmcsaExtensiveSearchEnabled && dot && (
                <div className={styles.extensiveSearchSection}>
                    <ExtensiveSearchList
                        searches={extensiveSearchList}
                        loading={extensiveSearchListLoading}
                        onViewDetails={viewExtensiveSearchDetails}
                        detailsLoading={extensiveSearchLoading}
                    />
                    {/* Request Extensive FMCSA Data button - below the table, aligned right */}
                    {!AuthService.isReadOnlyUser() && requestExtensiveFMCSADataButton()}
                </div>
            )}

            {/* Extensive FMCSA Search Drawer */}
            <ExtensiveFmcsaDrawer
                open={showExtensiveDrawer}
                onClose={() => setShowExtensiveDrawer(false)}
                data={extensiveSearchData}
                requestedAt={extensiveRequestedAt}
                legalName={fmcsaSearchData?.legalName}
                dot={fmcsaSearchData?.dotNumber}
            />
        </div>
    )
}

export default FmcsaVerification